import { useLanguageDirection } from "@hooks/useLanguageDirection";
import React, { useId, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

interface ShimmerBadgeFTDProps extends React.SVGProps<SVGSVGElement> {
  readonly isShimmering?: boolean;
}

// Badge dimensions (from original BG.svg: left=24.5, right=102.5, top=24.5, bottom=49.5)
const MIN_BADGE_WIDTH = 78; // 102.5 - 24.5
const BADGE_HEIGHT = 25; // 49.5 - 24.5
const CORNER_RADIUS = 4;

// Padding around the badge for shadows in the viewBox
const PADDING_LEFT = 8;
const PADDING_TOP = 8;
const PADDING_RIGHT = 8;
const PADDING_BOTTOM = 16; // For arrow + shadow

const TEXT_FONT_SIZE = 12;
const TEXT_FONT_WEIGHT = 700;
const TEXT_FONT_FAMILY = "Noto Sans Thai, system-ui, -apple-system, Segoe UI, Roboto, Arial";
const TEXT_PAD_X = 12; // Horizontal padding around text

// Text vertical center (relative to badge top, which is at PADDING_TOP)
const TEXT_CENTER_Y_OFFSET = BADGE_HEIGHT / 2 + 2; // +1 for optical centering

function measureTextPx(text: string, font: string): number {
  if (typeof document === "undefined") return 0;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  if (!ctx) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
}

/**
 * Generate badge path dynamically based on width.
 * Keeps the exact arrow shape from original BG.svg but allows badge to expand horizontally.
 */
function createBadgePathD(badgeWidth: number, leftX: number, topY: number): string {
  const rightX = leftX + badgeWidth;
  const bottomY = topY + BADGE_HEIGHT;
  const centerX = leftX + badgeWidth / 2;

  // All Y coordinates are relative to bottomY (which corresponds to 49.501 in original)
  // Arrow attach points (where arrow meets the badge bottom edge)
  const arrowRightAttach = centerX + 9.8987; // 73.3987 - 63.5
  const arrowLeftAttach = centerX - 9.8987; // 53.6013 - 63.5

  // Arrow right side curve (from attach point curving down toward tip)
  const arCp1X = centerX + 8.7647; // 72.2647 - 63.5
  const arCp2X = centerX + 7.684; // 71.184 - 63.5
  const arEndX = centerX + 6.9255; // 70.4255 - 63.5
  const arCp2Y = bottomY + 0.4813; // 49.9823 - 49.501
  const arEndY = bottomY + 1.3242; // 50.8252 - 49.501

  // Arrow tip line
  const tipLineX = centerX + 2.9732; // 66.4732 - 63.5
  const tipLineY = bottomY + 5.7157; // 55.2167 - 49.501

  // Arrow tip curve (creates the rounded bottom)
  const tipCp1X = centerX + 1.3843; // 64.8843 - 63.5
  const tipCp2X = centerX - 1.3843; // 62.1157 - 63.5
  const tipCpY = bottomY + 7.4814; // 56.9824 - 49.501
  const tipEndX = centerX - 2.9732; // 60.5268 - 63.5

  // Arrow left side
  const alStartX = centerX - 6.9255; // 56.5745 - 63.5
  const alCp1X = centerX - 7.684; // 55.816 - 63.5
  const alCp2X = centerX - 8.7647; // 54.7353 - 63.5

  return [
    // Start at right edge, slightly above bottom
    `M${rightX} ${bottomY - 3.999}`,
    // Bottom-right corner curve
    `C${rightX} ${bottomY - 1.7899} ${rightX - 1.791} ${bottomY} ${rightX - 4} ${bottomY}`,
    // Bottom edge to arrow right attach
    `H${arrowRightAttach}`,
    // Arrow right curve
    `C${arCp1X} ${bottomY} ${arCp2X} ${arCp2Y} ${arEndX} ${arEndY}`,
    // Arrow right line to tip
    `L${tipLineX} ${tipLineY}`,
    // Arrow tip curve - ROUNDED
    `C${tipCp1X} ${tipCpY} ${tipCp2X} ${tipCpY} ${tipEndX} ${tipLineY}`,
    // Arrow left line from tip
    `L${alStartX} ${arEndY}`,
    // Arrow left curve
    `C${alCp1X} ${arCp2Y} ${alCp2X} ${bottomY} ${arrowLeftAttach} ${bottomY}`,
    // Bottom edge to left corner
    `H${leftX + 4}`,
    // Bottom-left corner curve
    `C${leftX + 1.791} ${bottomY} ${leftX} ${bottomY - 1.7899} ${leftX} ${bottomY - 3.999}`,
    // Left edge up
    `V${topY + 4}`,
    // Top-left corner curve
    `C${leftX} ${topY + 1.791} ${leftX + 1.791} ${topY} ${leftX + 4} ${topY}`,
    // Top edge
    `H${rightX - 4}`,
    // Top-right corner curve
    `C${rightX - 1.791} ${topY} ${rightX} ${topY + 1.791} ${rightX} ${topY + 4}`,
    // Right edge down
    `V${bottomY - 3.999}`,
    "Z",
  ].join(" ");
}

export default function ShimmerBadgeFTD({ isShimmering = false, ...props }: ShimmerBadgeFTDProps) {
  const uid = useId().replaceAll(":", "");
  const { t, i18n } = useTranslation();
  const langDir = useLanguageDirection();
  const isRTL = langDir === "rtl";

  const paint0Id = `ftd-paint0-${uid}`;
  const filterId = `ftd-filter-${uid}`;
  const shimmerMaskId = `ftd-shimmer-mask-${uid}`;
  const shimmerCoreGradId = `ftd-shimmer-core-${uid}`;
  const shimmerGlowGradId = `ftd-shimmer-glow-${uid}`;
  const shimmerGlowBlurId = `ftd-shimmer-blur-${uid}`;

  const label = useMemo(() => t("free gift").toLocaleUpperCase(i18n.language), [t, i18n.language]);

  // Measure text width dynamically
  const [measuredTextW, setMeasuredTextW] = useState<number>(0);
  useLayoutEffect(() => {
    const font = `${TEXT_FONT_WEIGHT} ${TEXT_FONT_SIZE}px ${TEXT_FONT_FAMILY}`;
    setMeasuredTextW(measureTextPx(label, font));
  }, [label]);

  // Calculate badge width based on text
  const badgeWidth = useMemo(() => {
    const requiredWidth = Math.ceil(measuredTextW) + TEXT_PAD_X * 2;
    return Math.max(requiredWidth, MIN_BADGE_WIDTH);
  }, [measuredTextW]);

  // ViewBox dimensions (dynamic based on badge width)
  const viewBoxWidth = PADDING_LEFT + badgeWidth + PADDING_RIGHT;
  const viewBoxHeight = PADDING_TOP + BADGE_HEIGHT + PADDING_BOTTOM;

  // Badge position in viewBox
  const badgeLeftX = PADDING_LEFT;
  const badgeTopY = PADDING_TOP;
  const badgeCenterX = badgeLeftX + badgeWidth / 2;

  // Generate path dynamically
  const badgePathD = useMemo(
    () => createBadgePathD(badgeWidth, badgeLeftX, badgeTopY),
    [badgeWidth, badgeLeftX, badgeTopY],
  );

  // Text position
  const textCenterX = badgeCenterX;
  const textCenterY = badgeTopY + TEXT_CENTER_Y_OFFSET;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={viewBoxWidth}
      height={viewBoxHeight}
      viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
      fill="none"
      style={{ overflow: "visible" }}
      {...props}
    >
      <defs>
        {/* Gold gradient - vertical gradient across the badge */}
        <linearGradient
          id={paint0Id}
          x1={textCenterX}
          y1="0"
          x2={textCenterX}
          y2={badgeTopY + BADGE_HEIGHT + 8}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#FDC135" />
          <stop
            offset={1}
            stopColor="#EFDD75"
          />
        </linearGradient>

        {/* Drop shadow + inner shadow filter (no glow) */}
        <filter
          id={filterId}
          x="-50%"
          y="-50%"
          width="200%"
          height="200%"
          filterUnits="objectBoundingBox"
          colorInterpolationFilters="sRGB"
        >
          <feFlood
            floodOpacity={0}
            result="BackgroundImageFix"
          />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy={4} />
          <feGaussianBlur stdDeviation={3} />
          <feComposite
            in2="hardAlpha"
            operator="out"
          />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow"
            result="shape"
          />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy={1.5} />
          <feGaussianBlur stdDeviation={1.5} />
          <feComposite
            in2="hardAlpha"
            operator="arithmetic"
            k2={-1}
            k3={1}
          />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 1 0 0 0 0 0.945 0 0 0 0 0.725 0 0 0 0.78 0"
          />
          <feBlend
            mode="normal"
            in2="shape"
            result="effect2_innerShadow"
          />
        </filter>

        {/* Mask for shimmer (badge shape) */}
        <mask
          id={shimmerMaskId}
          maskUnits="userSpaceOnUse"
          x="0"
          y="0"
          width={viewBoxWidth}
          height={viewBoxHeight}
        >
          <rect
            x="0"
            y="0"
            width={viewBoxWidth}
            height={viewBoxHeight}
            fill="black"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d={badgePathD}
            fill="white"
          />
        </mask>

        {/* Shimmer glow gradient - synced with pulse */}
        <linearGradient
          id={shimmerGlowGradId}
          x1="0"
          y1="0"
          x2="1"
          y2="0"
          gradientTransform="rotate(5 0.5 0.5) translate(-1 0)"
        >
          <stop
            offset="0%"
            stopColor="#fff"
            stopOpacity="0"
          />
          <stop
            offset="30%"
            stopColor="#fff"
            stopOpacity="0"
          />
          <stop
            offset="44%"
            stopColor="#fff"
            stopOpacity="0.08"
          />
          <stop
            offset="50%"
            stopColor="#fff"
            stopOpacity="0.32"
          />
          <stop
            offset="56%"
            stopColor="#fff"
            stopOpacity="0.08"
          />
          <stop
            offset="70%"
            stopColor="#fff"
            stopOpacity="0"
          />
          <stop
            offset="100%"
            stopColor="#fff"
            stopOpacity="0"
          />
          <animateTransform
            begin="2.25s"
            attributeName="gradientTransform"
            additive="sum"
            type="translate"
            dur="2.5s"
            repeatCount="indefinite"
            values="0 0; 0 0; 2.2 0; 2 0; 0 0"
            keyTimes="0; 0.5; 0.75; 0.75; 1"
          />
        </linearGradient>

        {/* Shimmer core gradient - synced with pulse */}
        <linearGradient
          id={shimmerCoreGradId}
          x1="0"
          y1="0"
          x2="1"
          y2="0"
          gradientTransform="rotate(5 0.5 0.5) translate(-1 0)"
        >
          <stop
            offset="0%"
            stopColor="#fff"
            stopOpacity="0"
          />
          <stop
            offset="40%"
            stopColor="#fff"
            stopOpacity="0"
          />
          <stop
            offset="46%"
            stopColor="#fff"
            stopOpacity="0.18"
          />
          <stop
            offset="50%"
            stopColor="#fff"
            stopOpacity="0.92"
          />
          <stop
            offset="54%"
            stopColor="#fff"
            stopOpacity="0.18"
          />
          <stop
            offset="60%"
            stopColor="#fff"
            stopOpacity="0"
          />
          <stop
            offset="100%"
            stopColor="#fff"
            stopOpacity="0"
          />
          <animateTransform
            begin="2.25s"
            attributeName="gradientTransform"
            additive="sum"
            type="translate"
            dur="2.5s"
            repeatCount="indefinite"
            values="0 0; 0 0; 2.2 0; 2 0; 0 0"
            keyTimes="0; 0.5; 0.75; 0.75; 1"
          />
        </linearGradient>

        <filter
          id={shimmerGlowBlurId}
          x="-70%"
          y="-70%"
          width="240%"
          height="240%"
        >
          <feGaussianBlur stdDeviation={4} />
        </filter>
      </defs>

      {/* Base badge with shadow filter */}
      <g filter={`url(#${filterId})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d={badgePathD}
          fill={`url(#${paint0Id})`}
        />
      </g>

      {/* Shimmer layers (when pulsing) */}
      {isShimmering && (
        <>
          <rect
            x="0"
            y="0"
            width={viewBoxWidth}
            height={viewBoxHeight}
            fill={`url(#${shimmerGlowGradId})`}
            mask={`url(#${shimmerMaskId})`}
            filter={`url(#${shimmerGlowBlurId})`}
            opacity={0.5}
            style={{ mixBlendMode: "plus-lighter" }}
            transform="translate(-1 0)"
          />
          <rect
            x="0"
            y="0"
            width={viewBoxWidth}
            height={viewBoxHeight}
            fill={`url(#${shimmerCoreGradId})`}
            mask={`url(#${shimmerMaskId})`}
            opacity={0.6}
            style={{ mixBlendMode: "plus-lighter" }}
            transform="translate(-1 0)"
          />
        </>
      )}

      {/* Text */}
      <g pointerEvents="none">
        <text
          x={textCenterX}
          y={textCenterY}
          textAnchor="middle"
          dominantBaseline="middle"
          direction={isRTL ? "rtl" : "ltr"}
          fill="#000000"
          fontSize={TEXT_FONT_SIZE}
          fontFamily={TEXT_FONT_FAMILY}
          fontWeight={TEXT_FONT_WEIGHT}
        >
          {label}
        </text>
      </g>
    </svg>
  );
}
